import { Immutable } from "immer";
import * as t from "io-ts";

import { Products } from "../../models/catalogue";
import type { IProduct } from "../../models/catalogue";
import {
    BaseCompareTile,
    IBaseCompareTile,
    ICompareTile,
    isoTileID,
} from "../../models/product-compare";
import { check } from "../../models/utils";
import { type AppDispatch } from "../../store/reducers";
import { getChildFilterVariants } from "./selectors";
import { loadSelectedTileID, loadTiles } from "./thunks";

const synthesizeTile = (
    baseTile: IBaseCompareTile,
    productsByID: ReadonlyMap<number, IProduct>,
): ICompareTile => {
    const rootProducts = baseTile.product_compare_tile.product_ids
        .map((pid) => productsByID.get(Number(pid)))
        .filter((p): p is IProduct => p !== undefined);
    const annotated: ICompareTile = { ...baseTile, rootProducts };
    if (baseTile.product_compare_tile.child_filter) {
        return {
            ...annotated,
            childFilterProducts: getChildFilterVariants(
                annotated,
                baseTile.product_compare_tile.child_filter,
            ),
        };
    }
    return annotated;
};

type ITilesPrefillData = Immutable<{
    base_tiles_data: string;
    products_by_id_data: string;
}>;

export const prefillProductCompareTiles = (
    dispatch: AppDispatch,
    data: ITilesPrefillData,
): void => {
    let products;
    try {
        products = check(Products.decode(JSON.parse(data.products_by_id_data)));
    } catch (e) {
        console.error("Failed to parse SSR products_by_id_data:", e);
        return;
    }

    const BaseTiles = t.readonlyArray(BaseCompareTile);
    let baseTiles: readonly IBaseCompareTile[];
    try {
        baseTiles = check(BaseTiles.decode(JSON.parse(data.base_tiles_data)));
    } catch (e) {
        console.error("Failed to parse SSR base_tiles_data:", e);
        return;
    }

    const productsByID = new Map(products.map((p) => [Number(p.id), p]));
    const tiles = baseTiles.map((bt) => synthesizeTile(bt, productsByID));
    dispatch(loadTiles.fulfilled(tiles, "", baseTiles));
};

type ISelectionPrefillData = ITilesPrefillData &
    Immutable<{ initial_selected_tile_ids: readonly number[] }>;

export const prefillProductSelectionGrid = (
    dispatch: AppDispatch,
    data: ISelectionPrefillData,
): void => {
    prefillProductCompareTiles(dispatch, data);
    data.initial_selected_tile_ids.forEach((id) => {
        const tileID = isoTileID.wrap(id);
        dispatch(loadSelectedTileID.fulfilled(tileID, "", tileID));
    });
};
